import type { TOC } from '@ember/component/template-only';
import { imdbPersonUrl } from '../../utils/external-urls';

interface Signature {
  Args: { data: Record<string, unknown> | null | undefined };
}

interface PersonData {
  source?: 'tmdb' | 'spotify' | 'wikipedia';
  name?: string;
  biography?: string;
  birthday?: string;
  placeOfBirth?: string;
  profilePath?: string;
  imageUrl?: string;
  imdbId?: string;
  knownForDepartment?: string;
  genres?: string[];
  followerCount?: number;
  spotifyUrl?: string;
  // Wikipedia-specific fields
  description?: string;
  extract?: string;
  wikipediaUrl?: string;
}

function asPersonData(
  raw: Record<string, unknown> | null | undefined
): PersonData {
  const d = (raw ?? {}) as PersonData;
  d.genres = d.genres ?? [];
  return d;
}

function formatFollowers(n: number | undefined): string {
  if (!n) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

const PersonDetail: TOC<Signature> = <template>
  {{#let (asPersonData @data) as |d|}}
    <div class="flex flex-col gap-3">
      {{#if d.knownForDepartment}}
        <span
          class="badge badge-primary badge-sm"
        >{{d.knownForDepartment}}</span>
      {{/if}}

      {{#if d.description}}
        <span class="badge badge-ghost badge-sm">{{d.description}}</span>
      {{/if}}

      {{#if d.biography}}
        <p class="text-base-content/80 line-clamp-4 text-sm">{{d.biography}}</p>
      {{/if}}

      {{#if d.extract}}
        <p class="text-base-content/80 line-clamp-6 text-sm">{{d.extract}}</p>
      {{/if}}

      <div class="flex flex-wrap gap-2">
        {{#if d.birthday}}
          <span class="badge badge-outline badge-sm">🎂
            {{d.birthday}}</span>
        {{/if}}
        {{#if d.placeOfBirth}}
          <span class="badge badge-ghost badge-sm">📍
            {{d.placeOfBirth}}</span>
        {{/if}}
      </div>

      {{#if d.genres.length}}
        <div class="flex flex-wrap gap-1">
          {{#each d.genres as |genre|}}
            <span class="badge badge-ghost badge-sm">{{genre}}</span>
          {{/each}}
        </div>
      {{/if}}

      {{#if d.followerCount}}
        <p class="text-base-content/60 text-xs">
          {{formatFollowers d.followerCount}}
          followers
        </p>
      {{/if}}

      <div class="flex flex-wrap gap-2">
        {{#if d.imdbId}}
          <a
            href={{imdbPersonUrl d.imdbId}}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-xs btn-outline"
          >
            IMDb ↗
          </a>
        {{/if}}
        {{#if d.spotifyUrl}}
          <a
            href={{d.spotifyUrl}}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-xs border-[#1DB954] bg-[#1DB954] text-white hover:border-[#17a349] hover:bg-[#17a349]"
          >
            Spotify ↗
          </a>
        {{/if}}
        {{#if d.wikipediaUrl}}
          <a
            href={{d.wikipediaUrl}}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-xs btn-outline"
          >
            Wikipedia ↗
          </a>
        {{/if}}
      </div>
    </div>
  {{/let}}
</template>;

export default PersonDetail;
